import {createApp, markRaw} from 'vue'
import {createPinia} from 'pinia'
import {registerSW} from 'virtual:pwa-register';

import App from '@/App.vue'
import router from '@/router'
import type {Router} from 'vue-router'

import '@/assets/fonts/fonts.css'

import {createI18n, useI18n} from "vue-i18n";
import {useAuthStore} from "@/stores/authentication-store";
import {useOrganizationStore} from "@/stores/organization/organization-store";
import type {FeatureToggle} from "@/constants/feature-toggle";

declare module 'pinia' {
    export interface PiniaCustomProperties {
        router: Router
    }
}

const i18n = createI18n({
    locale: 'nl-NL',
    fallbackLocale: 'en-US',
    legacy: false,
    allowComposition: true,
    warnHtmlMessage: false,
    datetimeFormats: {
        'nl-NL': {
            short: { day: '2-digit', month: '2-digit', year: 'numeric' },            // dd-MM-yyyy
            shortWithTime: { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }, // dd-MM-yyyy HH:mm
            time: { hour: '2-digit', minute: '2-digit' }, // HH:mm
        },
        'de-DE': {
            short: { day: '2-digit', month: '2-digit', year: 'numeric' },            // dd.MM.yyyy
            shortWithTime: { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }, // dd.MM.yyyy HH:mm
            time: { hour: '2-digit', minute: '2-digit' }, // HH:mm
        },
        'fr-FR': {
            short: { day: '2-digit', month: '2-digit', year: 'numeric' },            // dd/MM/yyyy
            shortWithTime: { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }, // dd/MM/yyyy HH:mm
            time: { hour: '2-digit', minute: '2-digit' }, // HH:mm
        },
        'en-US': {
            short: { month: '2-digit', day: '2-digit', year: 'numeric' },            // MM/dd/yyyy
            shortWithTime: { month: '2-digit', day: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }, // MM/dd/yyyy HH:mm
            time: { hour: '2-digit', minute: '2-digit' }, // HH:mm
        }
    },
    numberFormats: {
        'nl-NL': {
            euro: { style: 'currency', currency: 'EUR', minimumFractionDigits: 2 }, // € 23,50
        },
        'de-DE': {
            euro: { style: 'currency', currency: 'EUR', minimumFractionDigits: 2 }, // 23,50 €
        },
        'fr-FR': {
            euro: { style: 'currency', currency: 'EUR', minimumFractionDigits: 2 }, // 23,50 €
        },
        'en-US': {
            euro: { style: 'currency', currency: 'EUR', minimumFractionDigits: 2 }, // € 23.50
        }
    }
})

const pinia = createPinia()

pinia.use(({store}) => {
    store.router = markRaw(router)
});

const app = createApp(App, {
    setup() {
        const {t} = useI18n() // call `useI18n`, and spread `t` from  `useI18n` returning
        return {t} // return render context that included `t`
    },
})

// this can be called in the template as v-check-claim="['resource.action']" only on standard html elements
// for example: v-check-claim="['organization-settings.read']"
app.directive('checkClaim', (el, binding) => {
    const claims: string[] = binding.value
    const authStore = useAuthStore()
    if (!authStore.hasClaims(claims)) {
        el.hidden = true;
    }
})

// this can be called in the template as v-check-any-claim="['resource.action', resource.another-action]" only on standard html elements
// for example: v-check-any-claim="['organization-settings.read', 'organization-settings.limited-read']"
app.directive('checkAnyClaim', (el, binding) => {
  const claims: string[] = binding.value
  const authStore = useAuthStore()
  if (!authStore.hasAtLeastOneClaim(claims)) {
    el.hidden = true;
  }
})

app.directive('checkFeatureToggle', (el, binding) => {
    const feature: FeatureToggle = binding.value
    const organizationStore = useOrganizationStore()
    if (!organizationStore.hasFeature(feature)) {
        el.hidden = true;
    }
})

registerSW({ immediate: true })

app.use(i18n)
app.use(pinia)
app.use(router)

app.mount('#app')
